// Admin v2 — adat-segédek: a users/{uid} dokumentum egységesítése (webes admin + mobilapp mezői),
// szerepek, dátumok és számformátumok.
import { Timestamp } from 'firebase/firestore';
import type { Tone } from './ui';

export type Role = 'admin' | 'venueOperator' | 'coach' | 'user';

export interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
  isAdmin: boolean;
  isVenueOperator: boolean;
  isCoach: boolean;
  isPremium: boolean;
  venueIds: string[];
  city: string;
  bio: string;
  createdAt: Date | null;
  lastActive: Date | null;
  dqScore: number;
  dqLevel: number;
  currentXP: number;
  totalXP: number;
  totalMatches: number;
  wins: number;
  losses: number;
  averagePPR: number;
  checkoutPercentage: number;
  highestCheckout: number;
  total180s: number;
}

/** Firestore Timestamp, ISO-szöveg, szám vagy {seconds} → Date */
export function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'seconds' in v) {
    return new Date(((v as { seconds: number }).seconds) * 1000);
  }
  return null;
}

const num = (v: unknown, d = 0) => (typeof v === 'number' && isFinite(v) ? v : d);
const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function normalizeUser(uid: string, v: Record<string, unknown>): AdminUser {
  const ob = (v.onboarding as Record<string, unknown>) ?? {};
  const venueIds = Array.isArray(v.venueIds) ? (v.venueIds as string[]) : v.venueId ? [String(v.venueId)] : [];
  return {
    uid,
    email: str(v.email),
    displayName: str(v.displayName) || str(v.fullName) || str(v.username) || str(v.email).split('@')[0] || 'Névtelen',
    username: str(v.username),
    avatarUrl: str(v.avatarUrl) || str(v.photoURL) || null,
    isAdmin: v.isAdmin === true,
    isVenueOperator: v.isVenueOperator === true,
    isCoach: v.isCoach === true,
    isPremium: v.isPremium === true,
    venueIds,
    city: str(ob.city) || str(v.city),
    bio: str(ob.bio) || str(v.bio),
    createdAt: toDate(v.createdAt),
    lastActive: toDate(v.lastActive),
    dqScore: num(v.dqScore),
    dqLevel: num(v.dqLevel, 1),
    currentXP: num(v.currentXP),
    totalXP: num(v.totalXP),
    totalMatches: num(v.totalMatches),
    wins: num(v.wins),
    losses: num(v.losses),
    averagePPR: num(v.averagePPR),
    checkoutPercentage: num(v.checkoutPercentage),
    highestCheckout: num(v.highestCheckout),
    total180s: num(v.total180s),
  };
}

export function roleOf(u: Pick<AdminUser, 'isAdmin' | 'isVenueOperator' | 'isCoach'>): Role {
  if (u.isAdmin) return 'admin';
  if (u.isVenueOperator) return 'venueOperator';
  if (u.isCoach) return 'coach';
  return 'user';
}

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  venueOperator: 'Üzemeltető',
  coach: 'Edző',
  user: 'Felhasználó',
};

export const ROLE_TONE: Record<Role, Tone> = {
  admin: 'cyan',
  venueOperator: 'gold',
  coach: 'purple',
  user: 'gray',
};

/** Szerep-váltás mezői a users dokumentumon (a régi admin logikájával egyezően). */
export function roleFields(role: Role, keepVenueIds: string[] = []): Record<string, unknown> {
  return {
    isAdmin: role === 'admin',
    isVenueOperator: role === 'venueOperator',
    isCoach: role === 'coach',
    role,
    venueIds: role === 'venueOperator' ? keepVenueIds : [],
  };
}

/* ---------- formázás ---------- */
export const fmtInt = (n: number) => Math.round(n).toLocaleString('hu-HU').replace(/ /g, ' ');

export const fmtDate = (d: Date | null) =>
  d ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}` : '—';

export const fmtDateDot = (d: Date | null) => (d ? `${fmtDate(d)}.` : '—');

export function timeAgo(d: Date | null): string {
  if (!d) return '';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'most';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} perce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} órája`;
  const dd = Math.floor(h / 24);
  if (dd < 30) return `${dd} napja`;
  const mo = Math.floor(dd / 30);
  if (mo < 12) return `${mo} hónapja`;
  return `${Math.floor(mo / 12)} éve`;
}

export function lastActiveText(d: Date | null): string {
  if (!d) return 'nincs adat';
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return same ? `ma ${hm}` : `${fmtDateDot(d)} ${hm}`;
}

/** Aktív: az elmúlt 30 napban használta az appot. */
export const isActive = (u: AdminUser) => Boolean(u.lastActive && Date.now() - u.lastActive.getTime() < 30 * 864e5);

export function pctDelta(cur: number, prev: number): { text: string; up: boolean } | null {
  if (prev <= 0) return cur > 0 ? { text: `${cur} új ebben a hónapban`, up: true } : null;
  const p = Math.round(((cur - prev) / prev) * 100);
  return { text: `${Math.abs(p)}% az előző hónaphoz képest`, up: p >= 0 };
}
